import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import { i18next, dialogs, customization, globals, template } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import upsell from 'src/js/modules/upsell';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import AdvancedColorsSettingsDialog from '../components/advanced-colors-settings-dialog';
import segment from '../../analytics/segment';
import useInjectPaletteToTemplate from '../hooks/use-inject-palette-to-template';
import helpers from '../helpers';
import triggerIds from '../constants/trigger-ids';
import dialogTypes from '../../dialogs/constants/dialog-types';
import constants from '../constants';
import { colorToSurfaceMap } from '../constants/colors-maps';

const {
  areAdvancedColorsChanged,
  generatePalette,
  resetAdvancedColor,
  getAdvancedColorsSections,
  updatePalettesWith,
  updateCustomPalettes,
  getSelectorByGlobalId,
  setCurrentPalette,
} = helpers;

const { GLOBAL_HEADER_CODE } = constants;

const { ColorTile } = designSystem;

const {
  hostingPackageFeatureNames: { ADVANCED_COLOR_CUSTOMIZATION },
} = subscriptionManagerConstants;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const getColorSchemeOptions = (colors, advancedColors = {}) => {
  const colorSchemes = [
    ['ws-light-0', i18next.t('Light color scheme')],
    ['ws-light-1', i18next.t('Light-grey color scheme')],
    ['ws-dark-0', i18next.t('Dark color scheme')],
    ['ws-dark-1', i18next.t('Dark-grey color scheme')],
    ['ws-primary-0', i18next.t('Primary color scheme')],
    ['ws-secondary-0', i18next.t('Secondary color scheme')],
  ];

  return colorSchemes.map(([colorKey, label]) => {
    const surfaceKey = colorToSurfaceMap[colorKey];
    const advancedBackgroundColorKey = `wsColor${surfaceKey}Background`;
    const backgroundColor = advancedColors[advancedBackgroundColorKey] || colors[colorKey];

    return {
      label,
      value: colorKey,
      iconComponent: <ColorTile size="medium" backgroundColor={backgroundColor} />,
    };
  });
};

const getCaptions = () => ({
  title: i18next.t('Edit color scheme'),
  description: i18next.t(
    'Changes you make here will affect all blocks of your website that use this color scheme'
  ),
  breadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
  tagLabel: i18next.t('PRO'),
  cancelButton: i18next.t('Cancel'),
  submitButton: i18next.t('Save'),
  searchPlaceholder: i18next.t('Search...'),
  emptyStateTitle: i18next.t('Sorry!'),
  emptyStateSubtitle: i18next.t('Nothing was found'),
});

const filterColorSections = (colorsSections, searchQuery) => {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (!normalizedQuery) return colorsSections;

  return colorsSections
    .map((section) => {
      const isSectionFullyMatched = section.title.toLowerCase().includes(normalizedQuery);
      if (isSectionFullyMatched) {
        return section;
      }

      const matchingItems = section.colorItems.filter((item) =>
        item.title.toLowerCase().includes(normalizedQuery)
      );
      return matchingItems.length ? { ...section, colorItems: matchingItems } : null;
    })
    .filter(Boolean);
};

const AdvancedColorsSettingsContainer = ({
  colorKey: initialColorKey,
  scrollTop = null,
  headerTextContentVisibility = true,
  triggerId,
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const {
    offsetX,
    offsetY,
    editPalette,
    editColorSchemePalette,
    activeColorSections: initialActiveColorSections,
    isDeveloperMode,
    editingButtonPresetId,
    searchQuery: initialSearchQuery = '',
  } = getSharedData();

  const dispatch = useDispatch();
  const contentRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [dialogPosition, updatePosition] = useState({
    offsetX,
    offsetY,
  });
  const [colorKey, setColorKey] = useState(initialColorKey);
  const [isVisibleHeaderTextContent, setIsVisibleHeaderTextContent] = useState(
    headerTextContentVisibility
  );

  const colorPaletteConfig = useSelector(customization.selectors.getColorPaletteConfig);
  const activeButtonPreset = useSelector((state) =>
    editingButtonPresetId
      ? customization.selectors.getButtonPreset(state, editingButtonPresetId)
      : customization.accessors.getCurrentButtonPreset(state)
  );
  const customColorPalettes = useSelector(customization.selectors.getCustomColorPalettes);
  const customPaletteTemplate = useSelector(customization.selectors.getColorPaletteTemplate);
  const colorPaletteVariable = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.COLOR_PALETTE_VARIABLE)
  );

  const limits = getCurrentPackageFeatureLimits();
  const isAdvancedColorCustomizationAvailable =
    limits.advancedColorCustomization.available || isDeveloperMode;

  const [featureFlags] = useFeatureFlags(['decoration_text_formatting']);
  const { decoration_text_formatting: isDecorationTextFormattingEnabled } = featureFlags;

  const colorPalette = editColorSchemePalette || editPalette;
  const { colors, advancedColors } = colorPalette;
  const colorSchemeOptions = getColorSchemeOptions(colors, advancedColors);
  const defaultSelectOption = colorSchemeOptions.find((option) => option.value === colorKey);

  const colorsSections = useMemo(
    () =>
      getAdvancedColorsSections({
        colorKey,
        palette: colorPalette,
        activeButtonPreset,
        options: {
          isDecorationTextFormattingEnabled,
        },
      }),
    [activeButtonPreset, colorKey, colorPalette, isDecorationTextFormattingEnabled]
  );

  const filteredColorsSections = useMemo(
    () => filterColorSections(colorsSections, searchQuery),
    [colorsSections, searchQuery]
  );

  const [activeColorSections, setActiveColorSections] = useState(
    initialActiveColorSections || colorsSections.map(({ id }) => id)
  );

  const captions = getCaptions();

  const injectPaletteToTemplate = useInjectPaletteToTemplate();

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const updateSharedData = (customData = {}) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const handleColorSelect = (selectedColor) => {
    const select = () => {
      updateSharedData({
        searchQuery,
        activeColorSections,
      });

      dispatch(
        dialogs.actions.show(dialogTypes.ADVANCED_COLOR_PICKER_DIALOG, {
          ...selectedColor,
          triggerId,
          backgroundColorKey: colorKey,
          scrollTop: contentRef.current.scrollTop,
          headerTextContentVisibility: isVisibleHeaderTextContent,
        })
      );
    };

    if (isDeveloperMode) {
      select();
      return;
    }

    upsell.operations
      .requestFeatureAvailability(ADVANCED_COLOR_CUSTOMIZATION)
      .then(select)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  };

  const handleColorReset = ({ colorKey: targetColorKey, variableCategory, variableId }) => {
    const reset = () => {
      const updatedPalette = resetAdvancedColor({
        colorKey: targetColorKey,
        backgroundColorKey: colorKey,
        colorPalette,
        activeButtonPreset,
      });

      const compiledPalette = generatePalette(updatedPalette, colorPaletteConfig);
      injectPaletteToTemplate(compiledPalette);
      updateSharedData({
        editColorSchemePalette: compiledPalette,
      });

      trackEvent(events.DESIGN_SETTINGS_RESET_TRIGGER_CLICKED, {
        dialogId: dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG,
        triggerId: triggerIds.ITEM_COLOR,
        colorsAdjusted: [targetColorKey],
        variableCategory,
        variableId,
      });
    };

    if (isDeveloperMode) {
      reset();
      return;
    }

    upsell.operations
      .requestFeatureAvailability(ADVANCED_COLOR_CUSTOMIZATION)
      .then(reset)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  };

  const closeDialog = () => {
    if (editingButtonPresetId) {
      dispatch(
        dialogs.actions.show(dialogTypes.BUTTON_PRESET_SETTINGS, {
          presetId: editingButtonPresetId,
        })
      );
      return;
    }

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
        triggerId: triggerIds.COLOR_SCHEME_SETTINGS,
        colorPreset: editPalette.id,
      })
    );
  };

  const handleCancel = () => {
    injectPaletteToTemplate(editPalette);
    updateSharedData({
      editPalette,
      editColorSchemePalette: null,
      isDeveloperMode: false,
      searchQuery: '',
      activeColorSections: null,
    });
    onDialogCancel();
    closeDialog();
  };

  const handleSubmit = () => {
    const advancedColorAdjusted = areAdvancedColorsChanged(editPalette, colorPalette);

    if (editingButtonPresetId && advancedColorAdjusted) {
      const updatedPalettes = updatePalettesWith(colorPalette, customColorPalettes);
      updateCustomPalettes(customColorPalettes, updatedPalettes);

      globals.operations.changeGlobalContent(
        GLOBAL_HEADER_CODE,
        (global) => {
          const transformations = [];
          const colorSelector = getSelectorByGlobalId(colorPaletteVariable, GLOBAL_HEADER_CODE);
          const setCurrentPaletteCurry = (globalItem) =>
            setCurrentPalette(globalItem, colorPalette, customPaletteTemplate, colorSelector);
          transformations.push(setCurrentPaletteCurry);

          return compose(...transformations)(global);
        },
        false
      );
    }

    updateSharedData({
      editPalette: colorPalette,
      editColorSchemePalette: null,
      isDeveloperMode: false,
      searchQuery: '',
      activeColorSections: null,
    });
    onDialogMainAction({
      colorScheme: colorKey,
      advancedColorAdjusted,
    });
    closeDialog();
  };

  const handleSearchQueryChange = (query) => {
    const filteredSections = filterColorSections(colorsSections, query);
    const searchTerm = query.trim();

    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setIsVisibleHeaderTextContent(true);

    setSearchQuery(searchTerm);

    if (searchTerm) {
      setActiveColorSections([
        ...new Set([...activeColorSections, ...filteredSections.map(({ id }) => id)]),
      ]);

      trackEvent(events.DESIGN_SETTINGS_SEARCH_PERFORMED, {
        sourceId: 'advanced-color-settings',
        searchTerm,
      });
    }
  };

  const onSearchFocus = () => {
    trackEvent(events.DESIGN_SETTINGS_SEARCH_CLICKED, {
      sourceId: 'advanced-color-settings',
    });
  };

  const handleColorSchemeChange = (option) => {
    const prevColorScheme = colorKey;
    setColorKey(option.value);

    trackEvent(events.DESIGN_SETTINGS_COLOR_SCHEME_CHANGED, {
      prevColorScheme,
      selectedColorScheme: option.value,
    });
  };

  useEffect(() => {
    if (!initialActiveColorSections) {
      setActiveColorSections(colorsSections.map(({ id }) => id));
    }
  }, [colorsSections, initialActiveColorSections]);

  useEffect(() => {
    const timeout = bowser.safari ? 100 : 0;

    if (scrollTop && contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollTo(0, scrollTop);
      }, timeout);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [contentRef.current]);

  useEffect(() => {
    const element = contentRef?.current;

    if (!element) return;

    const scrollHandler = () => {
      const scrollBottom = element.scrollHeight - element.clientHeight;
      const isAtBottom = element.scrollTop >= scrollBottom - 1;

      if (activeColorSections.length < 1) {
        setIsVisibleHeaderTextContent(true);

        return;
      }

      if (isAtBottom) return; // Skip if at bottom to avoid flicker

      if (element.scrollTop > 150) {
        setIsVisibleHeaderTextContent(false);
      } else if (element.scrollTop < 50) {
        setIsVisibleHeaderTextContent(true);
      }
    };

    element.addEventListener('scroll', scrollHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener('scroll', scrollHandler);
    };
  }, [activeColorSections]);

  return (
    <AdvancedColorsSettingsDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      colorsSections={filteredColorsSections}
      isAdvancedColorCustomizationAvailable={isAdvancedColorCustomizationAvailable}
      contentRef={contentRef}
      initialSearchQuery={initialSearchQuery}
      isVisibleHeaderTextContent={isVisibleHeaderTextContent}
      activeAccordionItems={activeColorSections}
      onToggleAccordionItem={setActiveColorSections}
      colorSchemeOptions={colorSchemeOptions}
      defaultColorSchemeOption={defaultSelectOption}
      onColorSchemeChange={handleColorSchemeChange}
      onColorSelect={handleColorSelect}
      onColorReset={handleColorReset}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onDragEnd={handleDragEnd}
      onSearchQueryChange={handleSearchQueryChange}
      onSearchFocus={onSearchFocus}
    />
  );
};

AdvancedColorsSettingsContainer.propTypes = {
  colorKey: PropTypes.string.isRequired,
  scrollTop: PropTypes.number,
  headerTextContentVisibility: PropTypes.bool,
  triggerId: PropTypes.string.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AdvancedColorsSettingsContainer;
